/** @jsxImportSource @emotion/react */
import React, { useMemo, useState } from 'react'
import Select, { StylesConfig } from 'react-select'

import { TooltipWrapper } from 'components/atoms/TooltipWrapper'

import {
  OTHER_WRITE_OFF_REASON,
  WRITE_OFF_CATEGORIES,
  WRITE_OFF_REASONS,
  WriteOffReason,
  writeOffCategoryLabel,
  writeOffNeedsReason,
} from 'ts-common/utils/writeOffReasons.utils'

interface Props {
  /** The write-off amount the reason explains. Zero means nothing to explain. */
  writeOff: string | number | null | undefined
  reason: string | null | undefined
  note?: string
  onReasonChange?: (value: string) => void
  onNoteChange?: (value: string) => void
  disabled?: boolean
  /** Set once a save was attempted with an amount but no reason. */
  hasError?: boolean
  /** Saved rows show the reason as text rather than a control. */
  readOnly?: boolean
  /**
   * The "Other" explanation was already given when the row was posted (it
   * lives on as a billing note), so an edit neither shows the field nor asks
   * for it again.
   */
  noteOnRecord?: boolean
  /** The list the reason comes from; the payer list by default. */
  reasons?: WriteOffReason<string>[]
  /** Shrunk to fit a table row; false renders a regular form field. */
  compact?: boolean
}

// A category heading toggles its reasons open; only a reason is a value.
type Option = {
  kind: 'category' | 'reason'
  value: string
  label: string
  category: string
  hint?: string
  count?: number
}

const ERROR_COLOR = '#dc3545'

// Below the bootstrap tooltip (1070), above the modal (1050), so a reason's tooltip shows over the menu.
const MENU_Z_INDEX = 1060

// An Adjustment reason as just its code ("CO-45"); the description is in the menu and tooltip.
const shortLabelOf = (label: string): string => label.split(' (')[0]

// The reason's definition, then its data-entry rule, as one tooltip.
const hintOf = (item: WriteOffReason<string>): string =>
  [item.guidance, item.rule && `Rule: ${item.rule}`].filter(Boolean).join('\n')

/**
 * The "why" beside a write-off. It appears only when there is an amount to
 * explain — a $0 write-off asks for nothing and the field stays out of the
 * way — and "Other" says nothing on its own, so it opens a note.
 *
 * One searchable select: categories are collapsible headings over their
 * reasons, and typing searches every reason (and category name) at once. Each
 * reason's definition and data-entry rule show on hover and, outside a table
 * row, under the field.
 *
 * Write-offs posted from an electronic remittance show here too: their reason
 * is taken from the payer's CARC codes when they are posted, and the row still
 * renders those codes as adjustment badges.
 */
export const WriteOffReasonField: React.FC<Props> = ({
  writeOff,
  reason,
  note = '',
  onReasonChange,
  onNoteChange,
  disabled,
  hasError,
  readOnly,
  noteOnRecord = false,
  reasons = WRITE_OFF_REASONS,
  compact = true,
}) => {
  const [menuOpen, setMenuOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState<string[]>([])
  // Tracked here rather than by the tooltip: the menu renders outside the field, which confuses its own hover state.
  const [hovered, setHovered] = useState(false)

  const selected = reasons.find((item) => item.value === reason)

  // The categories the list spans; a list within one category needs no headings.
  const categories = useMemo(
    () => WRITE_OFF_CATEGORIES.filter((option) => reasons.some((item) => item.category === option.value)),
    [reasons],
  )
  const grouped = categories.length > 1

  const options = useMemo<Option[]>(() => {
    const term = search.trim().toLowerCase()
    const toOption = (item: WriteOffReason<string>): Option => ({
      kind: 'reason',
      value: item.value,
      label: item.label,
      category: item.category,
      hint: hintOf(item),
    })

    if (!grouped) {
      return reasons.filter((item) => !term || item.label.toLowerCase().includes(term)).map(toOption)
    }

    return categories.flatMap((category) => {
      const categoryMatches = category.label.toLowerCase().includes(term)
      const members = reasons.filter((item) => item.category === category.value)
      const shown = term
        ? members.filter((item) => categoryMatches || item.label.toLowerCase().includes(term))
        : members

      if (!shown.length) {
        return []
      }

      const heading: Option = {
        kind: 'category',
        value: `category:${category.value}`,
        label: category.label,
        category: category.value,
        count: members.length,
      }
      const open = !!term || expanded.includes(category.value)

      return [heading, ...(open ? shown.map(toOption) : [])]
    })
  }, [categories, expanded, grouped, reasons, search])

  const styles = useMemo<StylesConfig<Option, false>>(
    () => ({
      control: (base) => ({
        ...base,
        ...(compact ? { minHeight: 26, fontSize: '0.75rem' } : {}),
        borderColor: hasError && !selected ? ERROR_COLOR : base.borderColor,
      }),
      valueContainer: (base) => (compact ? { ...base, padding: '0 4px' } : base),
      // A table cell is narrow: a small arrow leaves room for a code like "CO-45".
      dropdownIndicator: (base) => (compact ? { ...base, padding: 0, '& svg': { width: 14, height: 14 } } : base),
      singleValue: (base) => (compact ? { ...base, marginLeft: 0, marginRight: 0 } : base),
      indicatorSeparator: (base) => (compact ? { ...base, display: 'none' } : base),
      input: (base) => (compact ? { ...base, margin: 0, padding: 0 } : base),
      menuPortal: (base) => ({ ...base, zIndex: MENU_Z_INDEX }),
      menu: (base) => ({ ...base, minWidth: 280, fontSize: '0.75rem' }),
      option: (base, state) =>
        state.data.kind === 'category'
          ? {
              ...base,
              padding: '5px 10px',
              fontWeight: 600,
              backgroundColor: state.isFocused ? '#eef2f7' : '#f8f9fa',
              color: '#343a40',
              cursor: 'pointer',
            }
          : { ...base, padding: grouped ? '4px 10px 4px 26px' : '4px 10px' },
    }),
    [compact, grouped, hasError, selected],
  )

  // A saved reversal (negative amount) still shows the reason it reverses.
  if (!writeOffNeedsReason(writeOff as number) && !(readOnly && Number(writeOff) < 0)) {
    return null
  }

  if (readOnly) {
    return selected ? (
      <TooltipWrapper
        show
        text={grouped ? `${writeOffCategoryLabel(selected.category)}: ${selected.label}` : selected.label}>
        <div
          css={{
            fontSize: '0.75rem',
            color: '#6c757d',
            marginTop: '0.2rem',
            // One line in the row; the full reason is in the tooltip.
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
          }}>
          {shortLabelOf(selected.label)}
        </div>
      </TooltipWrapper>
    ) : null
  }

  const toggleCategory = (category: string) =>
    setExpanded((current) =>
      current.includes(category) ? current.filter((item) => item !== category) : [...current, category],
    )

  const openMenu = () => {
    setHovered(false)
    // Opens on the chosen reason's category so the current pick is in view.
    if (selected && !expanded.includes(selected.category)) {
      setExpanded((current) => [...current, selected.category])
    }
    setMenuOpen(true)
  }

  const handleChange = (option: Option | null) => {
    if (option?.kind === 'category') {
      toggleCategory(option.category)
      return
    }

    onReasonChange?.(option?.value ?? '')
    setSearch('')
    setMenuOpen(false)
    setHovered(false)
  }

  const value: Option | null = selected
    ? { kind: 'reason', value: selected.value, label: selected.label, category: selected.category }
    : null
  const otherNoteDue = reason === OTHER_WRITE_OFF_REASON && !noteOnRecord
  const controlClass = compact ? 'form-control form-control-sm' : 'form-control'

  return (
    // Outside a table row the definition and rule sit beside the select, wrapping under it when narrow.
    <div
      css={{
        marginTop: '0.25rem',
        ...(compact ? {} : { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '0.5rem 0.75rem' }),
      }}>
      <div css={compact ? undefined : { flex: '1 1 240px', minWidth: 0 }}>
        {/* In a table row the chosen reason's full name, definition and rule show on hover. */}
        <TooltipWrapper
          show={compact}
          visible={hovered && !!selected && !menuOpen}
          placement="top"
          maxWidth={320}
          text={() => (
            <div css={{ textAlign: 'left' }}>
              {selected && (
                <>
                  <div css={{ fontWeight: 600 }}>
                    {grouped ? `${writeOffCategoryLabel(selected.category)}: ${selected.label}` : selected.label}
                  </div>
                  {hintOf(selected) && <div css={{ whiteSpace: 'pre-line', marginTop: 4 }}>{hintOf(selected)}</div>}
                </>
              )}
            </div>
          )}>
          <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
            <Select<Option, false>
              aria-label="Write-off reason"
              classNamePrefix="react-select"
              placeholder="Reason…"
              isDisabled={disabled}
              isSearchable
              options={options}
              value={value}
              filterOption={null}
              inputValue={search}
              onInputChange={(text, meta) => {
                if (meta.action === 'input-change') {
                  setSearch(text)
                }
              }}
              menuIsOpen={menuOpen}
              closeMenuOnSelect={false}
              onMenuOpen={openMenu}
              onMenuClose={() => {
                setMenuOpen(false)
                setHovered(false)
                setSearch('')
              }}
              onChange={handleChange}
              isOptionSelected={(option) => option.kind === 'reason' && option.value === reason}
              formatOptionLabel={(option, { context }) =>
                context === 'value' ? (
                  <span>{shortLabelOf(option.label)}</span>
                ) : option.kind === 'reason' ? (
                  <TooltipWrapper
                    show={!!option.hint}
                    placement="right"
                    maxWidth={320}
                    text={() => <div css={{ whiteSpace: 'pre-line', textAlign: 'left' }}>{option.hint}</div>}>
                    {/* The whole row, so the tooltip opens beside the menu instead of over it. */}
                    <div>{option.label}</div>
                  </TooltipWrapper>
                ) : (
                  <span>
                    <i
                      className={`fas fa-chevron-${search.trim() || expanded.includes(option.category) ? 'down' : 'right'} mr-2`}
                      css={{ fontSize: '0.65rem', width: 10 }}
                    />
                    {option.label}
                    <span className="text-muted ml-1" css={{ fontWeight: 400 }}>
                      ({option.count})
                    </span>
                  </span>
                )
              }
              noOptionsMessage={() => 'No matching reason'}
              menuPortalTarget={document.body}
              menuPlacement="auto"
              styles={styles}
            />
          </div>
        </TooltipWrapper>
        {otherNoteDue && (
          <input
            type="text"
            className={controlClass}
            aria-label="Write-off explanation"
            placeholder="Explain…"
            value={note}
            disabled={disabled}
            onChange={(event) => onNoteChange?.(event.target.value)}
            css={{
              ...(compact ? { fontSize: '0.75rem', height: 'auto', padding: '0.15rem 0.25rem' } : {}),
              marginTop: '0.2rem',
              borderColor: hasError && !note.trim() ? ERROR_COLOR : undefined,
            }}
          />
        )}
        {hasError && (
          <div css={{ color: ERROR_COLOR, fontSize: compact ? '0.7rem' : '0.8rem', marginTop: '0.15rem' }}>
            {otherNoteDue && selected ? 'An explanation is required' : 'A reason is required'}
          </div>
        )}
      </div>
      {!compact && (selected?.guidance || selected?.rule) && (
        <div
          css={{
            flex: '1 1 260px',
            padding: '0.5rem 0.65rem',
            fontSize: '0.8rem',
            lineHeight: 1.4,
            color: '#495057',
            backgroundColor: '#f8f9fa',
            borderLeft: '3px solid #adb5bd',
            borderRadius: 4,
          }}>
          {selected.guidance && <div>{selected.guidance}</div>}
          {selected.rule && (
            <div
              css={{
                display: 'flex',
                gap: '0.4rem',
                ...(selected.guidance
                  ? { marginTop: '0.4rem', paddingTop: '0.4rem', borderTop: '1px solid #e9ecef' }
                  : {}),
              }}>
              <i className="fas fa-clipboard-check" css={{ color: '#6c757d', marginTop: '0.2rem' }} />
              <div>
                <span css={{ fontWeight: 600, color: '#343a40' }}>Rule: </span>
                {selected.rule}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
